import { useCallback, useEffect, useRef, useState } from "react";
import { triageAPI } from "../services/api";
import { isTerminalTriageState } from "../lib/triageStates";

const DEFAULT_POLL_INTERVAL_MS = 3000;
const DEFAULT_PAUSE_STATES = ["AWAITING_ACCOUNT"];

/**
 * Polls GET /api/v1/triage/:jobId via triageAPI.getStatus until the job
 * reaches a terminal state (FILED / FAILED), hits a pause state, or jobId is falsy.
 */
export default function useTriageJobStatus(
  jobId,
  { pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, pauseStates = DEFAULT_PAUSE_STATES } = {}
) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(jobId));
  const timeoutRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const next = await triageAPI.getStatus(jobId);
      setStatus(next);
      setError(null);
      return next;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  const shouldContinuePolling = useCallback(
    (state) => {
      if (isTerminalTriageState(state)) return false;
      if (pauseStates.includes(state)) return false;
      return true;
    },
    [pauseStates]
  );

  useEffect(() => {
    if (!jobId) {
      setStatus(null);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    const tick = async () => {
      try {
        const next = await fetchStatus();
        if (cancelled) return;
        if (next && shouldContinuePolling(next.state)) {
          timeoutRef.current = setTimeout(tick, pollIntervalMs);
        }
      } catch {
        // Stop polling on error; the caller can retry via refetch().
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [jobId, pollIntervalMs, fetchStatus, shouldContinuePolling]);

  return {
    status,
    error,
    isLoading,
    isTerminal: status ? isTerminalTriageState(status.state) : false,
    refetch: fetchStatus,
  };
}
